// MeinBüro theme entry point.
//
// This file is picked up automatically for the MeinBüro build (webpack resolves
// `@/theme` to `theme.meinbuero.ts` via the `.meinbuero` extension priority). It
// imports the shared base theme and deep-merges ONLY the values that differ from
// OrgaMax — mirroring the lang.js / lang.meinbuero.js pattern.
import { merge } from 'lodash'
import {
  baseCustomTheme,
  buildThemeApi,
  LIGHT_MODE_COLORS,
  DARK_MODE_COLORS,
  DeepPartial,
} from './theme.base'
import { ThemeConfig } from './theme-interface'

// Only the leaves that deviate from the base (OrgaMax) theme. Everything else is
// inherited from baseCustomTheme.
const meinbueroDelta: DeepPartial<ThemeConfig> = {
  primary: '#ee751c',
  primaryV2: '#ee751c',
  primaryLight: '#f58837',
  primaryBright: '#ee751e',
  primarySuperLight: '#faac71',
  primaryDark: '#ee751c',
  accent: '#1c7bf1',
  accentLight: '#4c83ff',
  accentSuperLight: '#AED0F6',
  accentDark: '#3264ff',
  colorPrimaryButtonBackground: '#ee751c',
  colorPrimaryButtonBackgroundHover: '#f58837',
  colorHint: 'rgba(50, 100, 255, 0.15)',
  backgroundColorPrimaryLight: 'rgba(232, 182, 64, 0.15)',
  lightBackground: '#f6f7fa',
  activeInputBorder: '#ee751c',
  borderOutlineColor: '#ee751c',
  finalizeHeaderColor: '#135899',
  headerColor: '#135899',
  iconColor: '#ee751e',
  iconFillColor: '#63849f',
  nextButton: '#ee751c',
  promotionContentBackgroundColor: '#135899',
  sidebarColor: '#135899',
  sidebarColorModal: '#06437B',
  sidebarColorModalRight: '#135899',
  sidebarStepCircleColor: '#DEE2ED',
  sidebarStepCircleColorActive: '#EE751C',
  navMenuItemBackgroundColor: '#06437b',
  navSubmenuItemColorActive: '#ee751c',
  navSubmenuImprintItemColorLight: '#adcce9',
  navBackgroundColor: '#135899',
  navTopBarBackgroundColor: '#06437b',
  // tint of primaryBright, meinbuero notes its tints as rgba like the other *Background values
  primaryBrightBackground: 'rgba(238, 117, 30, 0.15)',

  // MeinBüro-only colors
  darkerRed: '#e9435c',
  darkestBlue: '#212e52',

  icons: {
    plus: '\\F163',
    base: '#63849f',
  },
  Input: {
    borderColorActive: '#ee751c',
  },
  firstSteps: {
    welcomeSection: {
      backgroundColorPrimary: '#06437b',
      backgroundColorPrimaryLight: '#135899',
    },
  },
  signature: {
    moduleModal: {
      backgroundColor: '#06437b',
    },
  },
  trialSelection: {
    optionCard: {
      backgroundColor: '#06437b',
    },
  },
  topSalesStats: {
    top1: '#ee751c',
    top2: '#faac71',
  },
  typography: {
    hint: {
      color: '#747474',
    },
  },
  fontColors: {
    secondary: '#A4AABA',
  },
  borderColors: {
    defaultActive: '#ee751c',
    outline: '#ee751c',
  },
  palette: {
    primary: {
      main: '#EE7117',
      dark: '#DA6B18',
      light: '#F58737',
      contrastText: '#FFFFFF',
      50: '#FDF1E8',
      100: '#FCE3D2',
      200: '#F8C8A4',
      300: '#F5AC77',
      400: '#F58837',
      500: '#EE751C',
      550: '#DB6E1C',
      600: '#BE5E16',
      700: '#8F4611',
      800: '#472308',
    },

    error: {
      main: '#EE4A4D',
      dark: '#D1484B',
      light: '#FA6E6E',
      contrastText: '#FFFFFF',
    },

    warning: {
      main: '#E8B641',
      dark: '#BA9234',
      light: '#F1D38D',
      background: '#FAF0D9',
      contrastText: '#111111',
    },

    info: {
      main: '#0288D1',
      dark: '#01579B',
      light: '#03A9F4',
      contrastText: '#FFFFFF',
    },

    success: {
      main: '#4CAF50',
      dark: '#49A84D',
      light: '#68B96B',
      contrastText: '#FFFFFF',
    },

    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },

    divider: '#E0E1E4',

    text: {
      primary: '#414A5F',
      secondary: '#647190',
      disabled: 'rgba(100, 113, 144, 0.35)',
    },
  },
  tenant: {
    display: {
      label: '#aec7e3',
    },
    selection: {
      header: {
        backgroundColor: '#f6f7fa',
      },
      footer: {
        borderColor: 'rgba(222, 226, 237, 0.2)',
        shadow: '0 -5px 12px 0 rgba(0, 0, 0, 0.06)',
      },
      initials: {
        backgroundColor: '#ffffff',
        color: '#414a5f',
      },
    },
  },
  planUpgradeModal: {
    colors: {
      yellow: '#ffe200',
      blue: '#023e84',
    },
  },
  mobileNav: {
    demoHeader: '#538bcb',
    headerButton: '#e07b38',
    activeMenuItem: '#f1f2f5',
    inactiveMenuItem: '#adcce9',
  },
}

// Only the dark-mode background differs; light mode is identical to the base.
const meinbueroDarkModeDelta = {
  backgroundColor: {
    default: '#135899',
    medium: '#06437b',
    light: '#ee751e',
  },
}

// merge({}, …) so the shared baseCustomTheme / DARK_MODE_COLORS are never mutated.
const customTheme = merge({}, baseCustomTheme, meinbueroDelta) as ThemeConfig

const { createMuiThemeWithColorMode, muiTheme } = buildThemeApi(customTheme, {
  light: LIGHT_MODE_COLORS,
  dark: merge({}, DARK_MODE_COLORS, meinbueroDarkModeDelta),
})

export { FONT_SIZES, FONT_WEIGHTS, LINE_HEIGHTS } from './theme.base'
export { createMuiThemeWithColorMode, muiTheme }

export default customTheme
